<script lang="ts" setup>
    import type { Filters } from './SearchResultPreview.vue';

    const props = defineProps<{
        productHasResults: boolean | undefined | null;
        documentHasResults: boolean | undefined | null;
        newsHasResults: boolean | undefined | null;
        areaHasResults: boolean | undefined | null;
    }>();

    const { t } = useI18n();

    const filters = inject<Filters>('filters');

    // computed snapshot returns a new plain object on each change - the reactive object would not allow a 'previous state' in watch
    const filtersSnapshot = computed(() => ({ ...filters }));

    watch(filtersSnapshot, (newFilters, oldFilters) => {
        if (!filters) return;
        if (newFilters.all && !oldFilters.all) {
            filters.product = false;
            filters.document = false;
            filters.news = false;
            filters.area = false;
        } else if (
            (newFilters.product && !oldFilters.product) ||
            (newFilters.document && !oldFilters.document) ||
            (newFilters.news && !oldFilters.news) ||
            (newFilters.area && !oldFilters.area)
        ) {
            filters.all = false;
        } else if (
            !newFilters.all &&
            !newFilters.product &&
            !newFilters.document &&
            !newFilters.news &&
            !newFilters.area
        ) {
            // this needs to be debounced - otherwise the CSS glitches
            requestAnimationFrame(() => {
                filters.all = true;
            });
        }
    });
</script>

<template>
    <div
        v-if="
            filters &&
            [props.productHasResults, props.documentHasResults, props.newsHasResults, props.areaHasResults].filter(
                Boolean
            ).length > 1
        "
        class="filter"
    >
        <span class="headline">{{ t('searchPreview.filter') }}</span>
        <div class="filter-container">
            <PreviewFilter
                id="allFilter"
                v-model="filters.all"
                :label="t('searchPreview.filterType.all')"
            />

            <div v-if="props.productHasResults">
                <PreviewFilter
                    id="productFilter"
                    v-model="filters.product"
                    :label="t('searchPreview.filterType.product')"
                />
            </div>

            <div v-if="props.documentHasResults">
                <PreviewFilter
                    id="documentFilter"
                    v-model="filters.document"
                    :label="t('searchPreview.filterType.document')"
                />
            </div>

            <div v-if="props.newsHasResults">
                <PreviewFilter
                    id="newsFilter"
                    v-model="filters.news"
                    :label="t('searchPreview.filterType.news')"
                />
            </div>

            <div v-if="props.areaHasResults">
                <PreviewFilter
                    id="areaFilter"
                    v-model="filters.area"
                    :label="t('searchPreview.filterType.area')"
                />
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/search/search-preview-filter.scss';
</style>
